/**
 * "Your role for this system" — V104-G-12 (ADR-0077 §4).
 *
 * One intake question. The answer is PREFILLED from how the system is listed
 * (buying → we use it; building → we build it and use it) and only applies
 * once a person confirms it. Until then every duty is shown. Once confirmed,
 * a law's duties for other roles (e.g. a developer's documentation duty for a
 * system we only use) are left out of this system's obligations. Owner or
 * admin confirms (the server enforces it; PUT .../actor-roles).
 */
import { useEffect, useId, useState } from 'react';
import { Badge, Button, Checkbox } from '@aigovops/design-system';
import { ACTOR_ROLE_QUESTION_LABEL, CANONICAL_ROLES, prefillActorRoles, type CanonicalRole } from '@aigovops/core';
import { useQuery } from '@tanstack/react-query';
import { listWorkspaceMembers, setSystemActorRoles, type WsSystemRow } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceMutation } from '../hooks/useWorkspaceQuery';

/** The confirmed answer stored in classification_json, or null when unanswered. */
export function confirmedActorRoles(classificationJson: string | null): CanonicalRole[] | null {
  if (!classificationJson) return null;
  try {
    const v = JSON.parse(classificationJson) as { actor_roles?: unknown };
    if (!Array.isArray(v?.actor_roles) || v.actor_roles.length === 0) return null;
    return v.actor_roles.filter((r): r is CanonicalRole => (CANONICAL_ROLES as readonly string[]).includes(r as string));
  } catch {
    return null;
  }
}

/** Security review M4: who confirmed the answer and when (stored next to it). */
export function actorRolesConfirmation(classificationJson: string | null): { by: string; at: string } | null {
  if (!classificationJson) return null;
  try {
    const v = JSON.parse(classificationJson) as { actor_roles_confirmed_by?: unknown; actor_roles_confirmed_at?: unknown };
    return typeof v.actor_roles_confirmed_by === 'string' && typeof v.actor_roles_confirmed_at === 'string'
      ? { by: v.actor_roles_confirmed_by, at: v.actor_roles_confirmed_at }
      : null;
  } catch {
    return null;
  }
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

export function ActorRoleQuestion({ system, canEdit, onSaved, embedded = false, onDirtyChange }: {
  system: Pick<WsSystemRow, 'system_id' | 'lane' | 'classification_json'>;
  canEdit: boolean;
  onSaved: () => void;
  /** V112-3-10: inside a scope answer row (the row's heading names it; the save button is secondary). */
  embedded?: boolean;
  /** V112-3-10: a tick that isn't saved yet (the scope screen's leave guard holds while it is). */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const confirmed = confirmedActorRoles(system.classification_json);
  const confirmation = confirmed ? actorRolesConfirmation(system.classification_json) : null;
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const members = useQuery({
    queryKey: ['workspace-members', slug],
    queryFn: () => listWorkspaceMembers(slug),
    enabled: !!slug && !!confirmation,
    retry: false,
    staleTime: 60_000,
  });
  const confirmer = confirmation
    ? members.data?.members.find((m) => m.user_id === confirmation.by)
    : undefined;
  const confirmedLine = confirmation
    ? `Confirmed by ${confirmer?.display_name || confirmer?.email || 'a team member'} on ${new Date(confirmation.at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}.`
    : null;
  const suggested = prefillActorRoles(system.lane);
  const [picked, setPicked] = useState<CanonicalRole[]>(confirmed ?? suggested);
  const [message, setMessage] = useState<{ n: number; text: string; error?: boolean } | null>(null);
  // V112-3-10 (built-panel High): clearing a confirmed answer asks first; the answer saves on its own route.
  const [confirmClear, setConfirmClear] = useState(false);
  const statusId = `actor-role-status-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const save = useWorkspaceMutation(
    (slug, roles: CanonicalRole[]) => setSystemActorRoles(slug, system.system_id, roles),
    'classification:changed',
  );
  const busy = save.isPending;
  const changed = !confirmed || !same(picked, confirmed);
  // An unsaved tick: the picks differ from what is stored (or, unanswered, from the suggestion).
  const dirty = !same(picked, confirmed ?? suggested);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const say = (text: string, error = false) => setMessage((m) => ({ n: (m?.n ?? 0) + 1, text, error }));

  async function submit(roles: CanonicalRole[], clearing = false) {
    if (busy) return;
    if (roles.length === 0 && !clearing) {
      say(confirmed ? 'Pick at least one role, or use Clear answer.' : 'Pick at least one role to confirm.', true);
      return;
    }
    try {
      await save.mutateAsync(roles);
      say(roles.length ? 'Saved.' : 'Answer cleared. Every duty is shown.');
      onSaved();
      // The pressed button may go away after saving: keep the keyboard user in this question.
      requestAnimationFrame(() => document.getElementById(statusId)?.focus());
    } catch (err) {
      say(`That didn’t save: ${err instanceof Error ? err.message : String(err)}`, true);
    }
  }

  const statusLine = confirmed
    ? `${confirmedLine ? `${confirmedLine} ` : ''}Duties for other roles are left out.`
    : 'Every duty is shown until you confirm.';

  return (
    <fieldset aria-describedby={statusId} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', padding: 'var(--space-3)', margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
      {embedded ? (
        <>
          <legend className="sr-only">Your role for this system</legend>
          <span><Badge tone={confirmed ? 'accent' : 'neutral'}>{confirmed ? 'Confirmed' : 'Suggested'}</Badge></span>
        </>
      ) : (
        <legend style={{ padding: '0 var(--space-1)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)' }}>
          Your role for this system <Badge tone={confirmed ? 'accent' : 'neutral'}>{confirmed ? 'Confirmed' : 'Suggested'}</Badge>
        </legend>
      )}
      <p id={statusId} tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        {statusLine}
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
        {CANONICAL_ROLES.map((r) => (
          <Checkbox
            key={r}
            label={ACTOR_ROLE_QUESTION_LABEL[r]}
            checked={picked.includes(r)}
            disabled={!canEdit || busy}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setPicked((cur) => (on ? [...cur, r] : cur.filter((x) => x !== r)));
              setMessage(null);
            }}
          />
        ))}
      </div>
      <div role="status" aria-live="polite">
        {message ? (
          <p key={message.n} style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: message.error ? 'var(--danger-fg)' : 'var(--text)' }}>
            {message.text}
          </p>
        ) : null}
      </div>
      {canEdit ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {changed ? (
            <Button type="button" compact variant={embedded ? 'secondary' : 'primary'} aria-disabled={busy || undefined} onClick={() => void submit(picked)}>
              {busy ? 'Saving…' : confirmed ? 'Save role' : 'Confirm role'}
            </Button>
          ) : null}
          {confirmed && !confirmClear ? (
            <Button type="button" compact variant="ghost" aria-disabled={busy || undefined} onClick={() => { if (!busy) setConfirmClear(true); }}>
              Clear answer
            </Button>
          ) : null}
          {confirmed && confirmClear ? (
            <div role="group" aria-label="Clear your role answer?" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>Clear your answer? Every duty is shown again.</span>
              <Button type="button" compact variant="danger" aria-disabled={busy || undefined}
                onClick={() => { if (busy) return; setConfirmClear(false); setPicked(suggested); void submit([], true); }}>
                Clear it
              </Button>
              <Button type="button" compact variant="ghost" onClick={() => setConfirmClear(false)}>Keep it</Button>
            </div>
          ) : null}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>An owner or admin confirms the role.</p>
      )}
    </fieldset>
  );
}
